import { roleAtLeast, type MembershipRole } from '@ai-sales/shared'
import { NavLink, Outlet } from 'react-router'
import { useCurrentUser } from '../auth/session-context.js'
import {
  IconAgent,
  IconAntenna,
  IconFunnel,
  IconBox,
  IconAlert,
  IconChat,
  IconDocument,
  IconGauge,
  IconKey,
  IconList,
  IconPeople,
} from '../components/icon.js'
import { cn } from '../lib/cn.js'
import { useCurrentWorkspace } from '../workspace/workspace-context.js'
import { UserMenu } from './user-menu.js'
import { WorkspaceSwitcher } from './workspace-switcher.js'

interface NavItem {
  to: string
  label: string
  Icon: typeof IconChat
  /** Минимальная роль. Пункт просто не показывается, если роли не хватает. */
  minRole?: MembershipRole
  end?: boolean
  /** Открывает вторую группу меню — настройку, а не ежедневную работу. */
  startsGroup?: string
}

/**
 * Состав навигации.
 *
 * Здесь только то, за чем стоят работающие эндпоинты. Пунктов-заготовок
 * с надписью «скоро» нет намеренно: пустой раздел в меню выглядит как
 * сломанный, и разбираться, что именно не работает, приходится каждому,
 * кто откроет админку. Разделы появляются вместе с функциональностью.
 *
 * Разделение на две группы — не украшение: первая это то, что открывают
 * каждый день, вторая — то, что настраивают раз в месяц. Без разделения
 * восьмипунктовый список читается как восемь равнозначных дел.
 */
const NAV: NavItem[] = [
  { to: '', label: 'Состояние', Icon: IconGauge, end: true },
  { to: 'conversations', label: 'Диалоги', Icon: IconChat },
  { to: 'funnel', label: 'Воронка', Icon: IconFunnel },
  { to: 'members', label: 'Участники', Icon: IconPeople },
  /**
   * Агенты выше промптов с 23.09.2026. Порядок повторяет порядок работы:
   * заводят агента, а текст инструкции — его содержимое, и пишется он
   * в той же форме. Пока создание агента требовало готового промпта,
   * обратный порядок был верным; после правки он стал противоречить
   * тому, как человек ходит по экранам.
   */
  { to: 'agents', label: 'Агенты', Icon: IconAgent },
  { to: 'products', label: 'Товары', Icon: IconBox },
  { to: 'prompts', label: 'Промпты', Icon: IconDocument },
  {
    to: 'channels',
    label: 'Каналы',
    Icon: IconAntenna,
    minRole: 'admin',
    startsGroup: 'Настройка',
  },
  { to: 'credentials', label: 'Ключи интеграций', Icon: IconKey, minRole: 'admin' },
  { to: 'audit', label: 'Журнал действий', Icon: IconList, minRole: 'admin' },
  { to: 'jobs', label: 'Сбои задач', Icon: IconAlert, minRole: 'admin' },
]

export function AppShell() {
  const user = useCurrentUser()
  const workspace = useCurrentWorkspace()

  const items = NAV.filter((item) => !item.minRole || roleAtLeast(workspace.role, item.minRole))

  return (
    <div className="min-h-dvh md:grid md:grid-cols-[14rem_minmax(0,1fr)]">
      <aside className="flex flex-col gap-3 border-b border-line bg-surface p-3 md:sticky md:top-0 md:h-dvh md:border-r md:border-b-0">
        <div className="flex items-center gap-2.5 px-1.5 pt-1">
          <span
            aria-hidden="true"
            className="grid size-7 shrink-0 place-items-center rounded-md border border-accent font-mono text-[11px] font-semibold text-accent"
          >
            LP
          </span>
          <span className="text-[13px] font-semibold tracking-tight text-ink">LeadPilot</span>
        </div>

        <WorkspaceSwitcher />

        <nav aria-label="Разделы" className="flex flex-wrap gap-0.5 md:flex-col">
          {items.map((item) => (
            <div key={item.to || 'root'} className="contents">
              {item.startsGroup && (
                <p className="label-xs mt-3 hidden px-2.5 pb-1 md:block">{item.startsGroup}</p>
              )}
              <NavLink
                to={`/w/${workspace.id}/${item.to}`.replace(/\/$/, '')}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-md px-2.5 py-2 font-mono text-[12.5px] transition-colors',
                    isActive
                      ? 'bg-accent font-medium text-paper'
                      : 'text-ink-soft hover:bg-line-soft hover:text-ink',
                  )
                }
              >
                <item.Icon size={15} className="shrink-0" />
                {item.label}
              </NavLink>
            </div>
          ))}
        </nav>

        {/*
          Выбор темы живёт только в меню пользователя. Он был ещё и отдельным
          рядом кнопок здесь, и это оказалось хуже, а не лучше: одна настройка
          в двух местах заставляет гадать, отличаются ли они чем-нибудь.
        */}
        <div className="mt-auto hidden border-t border-line-soft pt-2 md:block">
          <UserMenu user={user} />
        </div>
      </aside>

      <div className="flex min-w-0 flex-col">
        {/*
          На узком экране боковая панель встаёт сверху и уже показывает
          название воркспейса, поэтому здесь остаётся только меню
          пользователя: второе, обрезанное название выглядело бы поломкой
        */}
        <header className="flex items-center justify-end border-b border-line bg-surface px-3 py-2 md:hidden">
          <div className="w-auto">
            <UserMenu user={user} />
          </div>
        </header>

        <main className="min-w-0 flex-1 px-4 py-5 md:px-7 md:py-7">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
